import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

export interface PersonMissingContractStartDate {
  id: string
  person: string | null
  person_id: string | null
  first_name: string | null
  surname: string | null
  email: string | null
  contract_start_date: string | null
  contract_end_date: string | null
  status: string | null
  applications: Array<{
    id: string
    application_id: string
    onboarded_at: string | null
  }>
}

export function usePeopleMissingContractStartDate() {
  return useQuery<PersonMissingContractStartDate[]>({
    queryKey: ["people-missing-contract-start-date"],
    queryFn: async () => {
      // Use JOIN query: people JOIN applications WHERE contract_start_date IS NULL AND current_stage = 'complete'
      // Order by contract_end_date DESC
      // Use specific relationship name to avoid ambiguity
      const { data, error } = await supabase
        .from("people")
        .select(
          `
          id,
          person,
          person_id,
          first_name,
          surname,
          email,
          contract_start_date,
          contract_end_date,
          status,
          applications!applications_person_id_fkey!inner(
            id,
            application_id,
            changed_stage_at
          )
        `,
        )
        .eq("status", "Active")
        .not("person_id", "is", null)
        .is("contract_start_date", null)
        .eq("applications.current_stage", "complete")
        .order("contract_end_date", { ascending: false, nullsFirst: false })

      if (error) {
        throw new Error(`Failed to fetch people: ${error.message}`)
      }

      if (!data || data.length === 0) {
        return []
      }

      // Group by person_id and keep only the most recent application per person
      // If multiple applications exist, use the one with the latest changed_stage_at (onboarded_at)
      const peopleMap = new Map<string, PersonMissingContractStartDate>()

      for (const row of data) {
        const personId = row.id
        const applications = Array.isArray(row.applications) ? row.applications : []

        if (applications.length === 0) continue

        // Find the application with the latest changed_stage_at (onboarded_at)
        const mostRecentApp = applications.reduce((latest, current) => {
          const latestDate = latest?.changed_stage_at
            ? new Date(latest.changed_stage_at).getTime()
            : 0
          const currentDate = current?.changed_stage_at
            ? new Date(current.changed_stage_at).getTime()
            : 0
          return currentDate > latestDate ? current : latest
        }, applications[0])

        // Only keep if we don't have this person yet, or if this application is more recent
        const existing = peopleMap.get(personId)
        if (!existing) {
          peopleMap.set(personId, {
            id: row.id,
            person: row.person,
            person_id: row.person_id,
            first_name: row.first_name,
            surname: row.surname,
            email: row.email,
            contract_start_date: row.contract_start_date,
            contract_end_date: row.contract_end_date,
            status: row.status,
            applications: [
              {
                id: mostRecentApp.id,
                application_id: mostRecentApp.application_id,
                onboarded_at: mostRecentApp.changed_stage_at,
              },
            ],
          })
        } else {
          // Compare onboarded_at dates to keep the most recent
          const existingOnboardedAt = existing.applications[0]?.onboarded_at
            ? new Date(existing.applications[0].onboarded_at).getTime()
            : 0
          const currentOnboardedAt = mostRecentApp.changed_stage_at
            ? new Date(mostRecentApp.changed_stage_at).getTime()
            : 0

          if (currentOnboardedAt > existingOnboardedAt) {
            peopleMap.set(personId, {
              ...existing,
              applications: [
                {
                  id: mostRecentApp.id,
                  application_id: mostRecentApp.application_id,
                  onboarded_at: mostRecentApp.changed_stage_at,
                },
              ],
            })
          }
        }
      }

      return Array.from(peopleMap.values())
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  })
}
